import React, {useCallback, useMemo} from 'react';
import {useTranslation} from 'react-i18next';
import {toPriceStr} from '@/utils';
import {normalizeProgressivePackageLocale} from '@fe-progressive/package-progressive/locale';
import type {PayConfig} from '@/services/pay.service';
import type {
  OpenDiskPayType,
  RechargeCoreHandle,
} from '@fe-progressive/package-progressive/recharge';
import {
  RechargeWebOpenDiskAmountPanel,
  RechargeWebOpenDiskPaytypeTabs,
  resolveRechargeWebOpenDiskAmountPanelLabels,
} from '@fe-progressive/package-progressive/recharge-web';
import type {RechargeWebAmountGridRow} from '@fe-progressive/package-progressive/recharge-web';
import type {
  ActivityPay,
  BalanceConfig,
} from './recharge-tab/recharge-tab.service';
import type {PayMethod, BalanceListItem} from './recharge.service';
import {
  calculateTotalAmount,
  getAmountOptionAndChannelBonusText,
} from './util/recharge-tab-bonus.utils';
import RechargeTab from './recharge-tab/recharge-tab';

export type OpenDiskSectionProps = {
  core: RechargeCoreHandle;
  enableCrypto: boolean;
  isWeekCard: boolean;
  openDiskPayType: OpenDiskPayType;
  onOpenDiskPayTypeChange: (v: OpenDiskPayType) => void;
  openDiskPresetValue: string;
  onOpenDiskPresetValueChange: (v: string) => void;
  canShowBalanceList: BalanceListItem[];
  payMethodItem: PayMethod | undefined;
  activeTab: ActivityPay | undefined;
  balanceConfig: BalanceConfig[];
  payConfig: PayConfig | null;
  useType: number;
  extraBounsBalanceIndex: number;
  truncateToDecimalPlaces: (n: number, d: number) => number;
  calculateTotalAmount: typeof calculateTotalAmount;
  /** UPI / 周卡金额区；与 Crypto 面板互斥，由本组件统一渲染 OpenDisk 面板 */
  upiOpenDiskAmount: {
    amountText: string;
    onAmountChange: (v: string) => void;
    balanceList: readonly RechargeWebAmountGridRow[];
    topNotice?: React.ReactNode;
    minMaxHint?: React.ReactNode;
    inputReadOnly?: boolean;
    amountGridClassName?: string;
    isWeekCard?: boolean;
  };
};

export const OpenDiskSection: React.FC<OpenDiskSectionProps> = ({
  core,
  enableCrypto,
  isWeekCard,
  openDiskPayType,
  onOpenDiskPayTypeChange,
  openDiskPresetValue,
  onOpenDiskPresetValueChange,
  canShowBalanceList,
  payMethodItem,
  activeTab,
  balanceConfig,
  payConfig,
  useType,
  extraBounsBalanceIndex,
  truncateToDecimalPlaces,
  calculateTotalAmount: calcTotal,
  upiOpenDiskAmount,
}) => {
  const {i18n} = useTranslation();
  const progressiveLocale = useMemo(
    () => normalizeProgressivePackageLocale(i18n.language),
    [i18n.language],
  );

  const openDiskLabels = useMemo(
    () => resolveRechargeWebOpenDiskAmountPanelLabels(progressiveLocale),
    [progressiveLocale],
  );

  const showCryptoAmountPanel =
    enableCrypto &&
    core.isCryptoExtensionActive() &&
    !isWeekCard &&
    openDiskPayType === 'CRYPTO';

  const showUpiAmountPanel = !(
    openDiskPayType === 'CRYPTO' &&
    !isWeekCard &&
    enableCrypto
  );

  /** 与 recharge 基准页一致：仅 OpenDisk（UPI/Crypto）能力开启且扩展可用时展示切换条；活动 Tab 始终展示 */
  const showOpenDiskPaytypeTabs =
    enableCrypto && core.isCryptoExtensionActive();

  const formatBalanceAmount = useCallback(
    (n: number) =>
      toPriceStr(n, {
        fixed: 0,
        showCurrency: false,
        thousands: true,
      }),
    [],
  );

  const getBonusLabel = useCallback(
    (row: RechargeWebAmountGridRow) => {
      if (isWeekCard) {
        return undefined;
      }
      return (
        getAmountOptionAndChannelBonusText(
          String(row.balance),
          payMethodItem,
          row as BalanceListItem,
          activeTab,
          useType,
          payConfig?.exchange,
        ) || undefined
      );
    },
    [isWeekCard, payMethodItem, activeTab, useType, payConfig?.exchange],
  );

  return (
    <div className="flex flex-col gap-3">
      {showOpenDiskPaytypeTabs ? (
        <RechargeWebOpenDiskPaytypeTabs
          value={openDiskPayType}
          onChange={onOpenDiskPayTypeChange}
        />
      ) : null}
      <RechargeTab onTabChange={() => {}} loading={false} useType={useType} />
      {showCryptoAmountPanel ? (
        <RechargeWebOpenDiskAmountPanel
          labels={openDiskLabels}
          locale={progressiveLocale}
          useDefaultEnterHint
          amountText={openDiskPresetValue}
          onAmountChange={onOpenDiskPresetValueChange}
          balanceList={canShowBalanceList}
          formatBalanceAmount={formatBalanceAmount}
          getBonusLabel={getBonusLabel}
          exchangeRateText={`1 USD ≈ ${payConfig?.exchange || '--'} INR`}
          processingFeeText={
            payConfig?.cryptoRechargeFee &&
            Number(payConfig.cryptoRechargeFee) > 0
              ? `${(payConfig.cryptoRechargeFee * 100).toFixed(0)}%`
              : undefined
          }
          amountCreditedText={`${truncateToDecimalPlaces(
            calcTotal(
              openDiskPresetValue,
              payMethodItem,
              canShowBalanceList[extraBounsBalanceIndex],
              activeTab,
              balanceConfig,
              useType,
              payConfig?.cryptoRechargeFee,
              payConfig?.exchange,
            ),
            2,
          ).toFixed(2)} USD`}
          currencySymbol="$"
        />
      ) : showUpiAmountPanel ? (
        <RechargeWebOpenDiskAmountPanel
          labels={openDiskLabels}
          locale={progressiveLocale}
          useDefaultEnterHint
          topNotice={upiOpenDiskAmount.topNotice}
          minMaxHint={upiOpenDiskAmount.minMaxHint}
          amountText={upiOpenDiskAmount.amountText}
          onAmountChange={upiOpenDiskAmount.onAmountChange}
          inputReadOnly={upiOpenDiskAmount.inputReadOnly}
          balanceList={upiOpenDiskAmount.balanceList}
          formatBalanceAmount={formatBalanceAmount}
          getBonusLabel={getBonusLabel}
          currencySymbol="₹"
          amountGridClassName={upiOpenDiskAmount.amountGridClassName}
          isWeekCard={upiOpenDiskAmount.isWeekCard}
        />
      ) : null}
    </div>
  );
};
